import { ArrowRight } from "lucide-react";
import ServiceSectionShell from "./ServiceSectionShell";

const cloudPath = [
  {
    step: "Assess",
    detail:
      "Infra, application, and cost profiling across current hosting, licensing, and data gravity.",
  },
  {
    step: "Architect",
    detail:
      "Landing zone design, security controls, and EHR-aware workload placement across AWS, Azure, or hybrid.",
  },
  {
    step: "Migrate",
    detail:
      "Staged lift, refactor, and retire—coordinated with clinical cutover windows and zero-downtime playbooks.",
  },
  {
    step: "Operate",
    detail:
      "FinOps, observability, and platform managed services so cost and uptime don't drift post-migration.",
  },
];

const wherePays = [
  {
    label: "Cost & licensing",
    detail:
      "Rationalize duplicated licenses, right-size compute, and move predictable workloads to reserved or committed pricing.",
  },
  {
    label: "EHR & imaging modernization",
    detail:
      "Migrate PACS archives, imaging pipelines, and EHR peripherals without clinical interruption.",
  },
  {
    label: "Disaster recovery",
    detail:
      "Cloud-anchored BDR with tested RTO/RPO targets and ransomware-resistant backup strategies.",
  },
];

export default function CloudServices() {
  return (
    <ServiceSectionShell
      kicker="Cloud Services"
      title="Cloud migration and modernization built around clinical uptime"
      lead="Moving healthcare workloads to the cloud isn't a lift-and-shift. We architect, migrate, and operate cloud platforms on AWS, Azure, and hybrid—tuned for EHR adjacency, imaging archives, and audit-grade controls."
      tone="tint"
    >
      <div className="rounded-2xl border border-shark-200 bg-white p-6 shadow-sm sm:p-7">
        <div className="flex items-center justify-between gap-3">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-complement-600">
            The cloud journey
          </p>
          <span className="hidden text-[10.5px] font-semibold uppercase tracking-[0.14em] text-shark-400 sm:inline">
            End-to-end ownership
          </span>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {cloudPath.map((s, i) => (
            <div key={s.step} className="relative flex items-start gap-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-complement-50 text-[13px] font-extrabold text-complement-700 ring-1 ring-complement-200">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div>
                <p className="text-[0.95rem] font-bold text-shark-900">
                  {s.step}
                </p>
                <p className="mt-1 text-[0.84rem] leading-relaxed text-shark-500">
                  {s.detail}
                </p>
              </div>
              {i < cloudPath.length - 1 && (
                <ArrowRight className="absolute -right-3 top-2 hidden h-4 w-4 text-shark-300 lg:block" />
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-center lg:gap-14">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-complement-600">
            Where cloud pays off
          </p>
          <h4 className="mt-3 text-[clamp(1.25rem,2vw,1.5rem)] font-bold leading-[1.2] tracking-tight text-shark-900">
            Three outcomes healthcare CFOs and CIOs actually see
          </h4>
          <p className="mt-4 text-[0.95rem] leading-[1.65] text-shark-500">
            We anchor cloud programs to measurable financial, clinical, and
            risk outcomes — not to vendor certifications or architecture
            diagrams.
          </p>
          <div className="mt-6 flex items-center gap-3 text-[10.5px] font-bold uppercase tracking-[0.16em] text-shark-400">
            <span>AWS</span>
            <span className="h-1 w-1 rounded-full bg-shark-300" aria-hidden />
            <span>Azure</span>
            <span className="h-1 w-1 rounded-full bg-shark-300" aria-hidden />
            <span>Hybrid</span>
          </div>
        </div>

        <ol className="divide-y divide-shark-200 overflow-hidden rounded-2xl border border-shark-200 bg-white shadow-sm">
          {wherePays.map((w, i) => (
            <li
              key={w.label}
              className="group flex items-start gap-5 p-5 transition-colors hover:bg-shark-50/60 sm:p-6"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-complement-50 text-[12.5px] font-extrabold text-complement-700 ring-1 ring-complement-200">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[1rem] font-bold text-shark-900">
                  {w.label}
                </p>
                <p className="mt-1.5 text-[0.88rem] leading-relaxed text-shark-500">
                  {w.detail}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </ServiceSectionShell>
  );
}
